import api from "./api";
import type { ConsultationsResponse, ConsultationDetail } from "../types/consultation";
import { FormTemplate } from "../types/forms";

export type ApiError =
  | { type: "serverValidation"; errors?: Record<string, string> }
  | { type: "network"; message?: string }
  | { type: "server"; message?: string };


export const getConsultationsByDate = async (date?: string): Promise<ConsultationsResponse> => {
  try {
    const params = date ? { date } : {};
    const response = await api.get<ConsultationsResponse>("/admin/consultations", {
      params,
      headers: { Accept: "application/json" },
    });
    console.log("Consultations fetched successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to fetch consultations:", err.response?.data || err);
    throw err;
  }
};


export const getConsultationById = async (id: number | string): Promise<{ form: ConsultationDetail }> => {
  if (!id) {
    throw { type: "serverValidation", errors: { id: "ID is required" } } as ApiError;
  }

  try {
    const response = await api.get<{ form: ConsultationDetail }>(`/admin/consultations/${id}`, {
      headers: { Accept: "application/json" },
    });
    console.log("Consultation fetched successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to fetch consultation:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};


export const getAllForms = async () => {
  try {
    const response = await api.get("/admin/forms", {
      headers: { Accept: "application/json" },
    });

    console.log("Forms fetched successfully", response.data);
    console.log("ALL FORMS", response.data)
    return response.data;

  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    console.error("Failed to fetch forms:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const getFormByUuid = async (uuid: string) => {
  if (!uuid) {
    throw { type: "serverValidation", errors: { uuid: "UUID is required" } } as ApiError;
  }

  try {
    const response = await api.get(`/admin/forms/${uuid}`, {
      headers: { Accept: "application/json" },
    });

    console.log("Form fetched successfully with uuid ", uuid, "below is the response", response.data);
    return response.data;

  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    console.error("Failed to fetch form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};


export const createForm = async (formData: any) => {
  if (!formData || !formData.title?.trim()) {
    throw { type: "serverValidation", errors: { title: "Form title is required" } } as ApiError;
  }

  try {
    const response = await api.post("/admin/forms", formData, {
      headers: { Accept: "application/json" },
    });
    console.log("Form created successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to create form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const saveForm = async (formUuid: string, formData: any) => {
  if (!formUuid) {
    throw { type: "serverValidation", errors: { uuid: "Form UUID is required" } } as ApiError;
  }
  try {
    const response = await api.post(`/admin/forms/${formUuid}/save`, formData, {
      headers: { Accept: "application/json" },
    });
    console.log("Form saved successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to save form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const updateForm = async (formUuid: string, formData: any) => {
  if (!formUuid) {
    throw { type: "serverValidation", errors: { uuid: "Form UUID is required" } } as ApiError;
  }

  try {
    const response = await api.put(`/admin/forms/${formUuid}`, formData, {
      headers: { Accept: "application/json" },
    });

    console.log("Form updated successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    console.error("Failed to update form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const deleteForm = async (formUuid: string) => {
  if (!formUuid) {
    throw { type: "serverValidation", errors: { uuid: "Form UUID is required" } } as ApiError;
  }
  try {
    const response = await api.delete(`/admin/forms/${formUuid}`, {
      headers: { Accept: "application/json" },
    });

    console.log("Form deleted successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    console.error("Failed to delete form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

interface ReorderRequest {
  sections?: {
    id: number;
    order: number;
  }[];
  fields?: {
    id: number;
    order: number;
  }[];
}

interface ReorderSectionsResponse {
  message: string;
  form: FormTemplate;
}

export const reorderItems = async (
  formUuid: string,
  payload: ReorderRequest
): Promise<ReorderSectionsResponse> => {
  if (!formUuid) {
    throw { type: "serverValidation", errors: { uuid: "Form UUID is required" } } as ApiError;
  }

  try {
    const response = await api.post<ReorderSectionsResponse>(
      `/admin/forms/${formUuid}/reorder`,
      payload,
      {
        headers: { Accept: "application/json" },
      }
    );

    console.log("Sections reordered successfully", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to reorder sections:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const addSection = async (formUuid: string, formTemplateId: number) => {
  if (!formUuid) throw { type: "serverValidation", errors: { uuid: "Form UUID is required" } } as ApiError;

  try {
    const response = await api.post(`/admin/forms/${formUuid}/templates`, {
      form_template_id: formTemplateId,
    }, {
      headers: { Accept: "application/json" },
    });

    console.log("Section added successfully", response.data);
    return response.data;
  } catch (err: any) {
    console.error("Failed to add section:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};


export const deleteSection = async (
  formUuid: string,
  sectionId: string | number
): Promise<void> => {
  if (!formUuid || !sectionId) {
    throw {
      type: "clientValidation",
      errors: { formUuid, sectionId },
    } as unknown as ApiError;
  }

  try {
    const response = await api.delete(`/admin/forms/${formUuid}/sections/${sectionId}`, {
      headers: { Accept: "application/json" },

    });
    console.log("Section deleted successfully", response.data);
  } catch (err: any) {
    console.error("Failed to delete section:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const addFields = async (sectionId: string | number, fields: any[]) => {
  try {
    const response = await api.post(`/admin/sections/${sectionId}/fields`, { fields }, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const saveGeneralQuestion = async (sectionId: string | number, data: any) => {
  try {
    const response = await api.post(`/admin/forms/sections/${sectionId}/general-question`, data, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const updateFieldContent = async (fieldId: string | number, content: string) => {
  try {
    const response = await api.post(`/admin/forms/fields/${fieldId}/content`, { content }, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    throw { type: "server", message: err.message } as ApiError;
  }
};

/** Update field title (e.g. Text Block / section label). Backend stores in field.options. */
export const updateFieldTitle = async (
  fieldId: string | number,
  title: string | null
): Promise<{ message: string; field: unknown }> => {
  try {
    const response = await api.post(
      `/admin/forms/fields/${fieldId}/title`,
      { title: title ?? "" },
      { headers: { Accept: "application/json" } }
    );
    return response.data;
  } catch (err: any) {
    console.error("Failed to update field title:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const updateFieldOptions = async (fieldId: string | number, options: string[]) => {
  try {
    const response = await api.post(`/admin/forms/fields/${fieldId}/options`, { options }, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const deleteFieldOrSection = async (type: "section" | "field", id: number) => {
  try {
    const response = await api.delete(`/admin/forms/section/destroy`, {
      data: { type, id },
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const getPublicForm = async (id: string | number) => {
  try {
    const response = await api.get(`/forms/consultation/${id}`, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    console.error("Failed to fetch public form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};

export const submitPublicForm = async (id: string | number, data: any) => {
  try {
    const response = await api.post(`/forms/consultation/${id}`, data, {
      headers: { Accept: "application/json" },
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }
    console.error("Failed to submit public form:", err.response?.data || err);
    throw { type: "server", message: err.message } as ApiError;
  }
};
